/**
 * Alarms Module - Actions
 *
 * Async actions for loading and managing alarm data via DeviceService
 */

import type { ActionContext } from 'vuex';
import type { RootState } from '@/store/types';
import type { AlarmsState } from './mutations';
import type { IAlarmData } from '@/models/dtos/AlarmDTO';
import { DeviceService } from '@/services/DeviceService';
import { AlarmDTO } from '@/models/dtos';

type Ctx = ActionContext<AlarmsState, RootState>;

/**
 * Payload for loading alarms history
 */
interface LoadAlarmsPayload {
  /** Gateway registry ID */
  gateway: string;
  /** Device ID */
  device: string;
}

export default {
  /**
   * Load alarm history for a specific device from the last 24 hours
   * Uses DeviceService which auto-injects auth token via ApiClient
   *
   * @param context - Vuex action context
   * @param payload - Gateway and device identifiers
   * @throws Error if API call fails
   */
  async loadAlarmsHistory(context: Ctx, payload: LoadAlarmsPayload): Promise<void> {
    try {
      const currentTime = Math.floor(Date.now() / 1000);
      const endTime = currentTime;
      const startTime = currentTime - 24 * 60 * 60; // Last 24 hours

      const data = await DeviceService.getAlarmsHistory(
        payload.gateway,
        payload.device,
        startTime,
        endTime
      );

      // Transform DTOs back to store format (IAlarmData interface)
      const dtos = AlarmDTO.fromApiResponseArray(data as any);
      const alarmsData: IAlarmData[] = dtos.map(dto => ({
        type: dto.type,
        threshold_ON: dto.thresholdOn ?? undefined,
        excess_value_ON: dto.valueOn ?? undefined,
        threshold_OFF: dto.thresholdOff ?? undefined,
        excess_value_OFF: dto.valueOff ?? undefined,
        active: dto.active,
        timestamp_ON: dto.start ?? undefined,
        timestamp_OFF: dto.end ?? undefined,
      }));

      context.commit('setAlarmHistory', alarmsData);
    } catch (error) {
      console.error('[Alarms] Error fetching alarms history:', error);
      throw error;
    }
  },

  /**
   * Clear all alarm data from state
   * @param context - Vuex action context
   */
  cleanData(context: Ctx): void {
    context.commit('resetData');
  },
};


